import type { RealtimeChannel } from "ably";
import { useCallback, useEffect, useRef } from "react";
import type { PublishGuardState } from "../core";
import { ABLY_CONFIG, getRuntimeConfig } from "../core";

export function useSafePublish(
	channel: RealtimeChannel | null,
	publishGuard: PublishGuardState,
	createConnectionError: () => Error,
) {
	const connectedRef = useRef(false);
	connectedRef.current = publishGuard.isConnected;

	const initialCheckRef = useRef(false);
	initialCheckRef.current = publishGuard.isReady;

	// The retry sequence spans several seconds of backoff. The hook owns the lifetime, so
	// callers get cancellation on unmount without threading a signal through every publish.
	const unmountedRef = useRef(false);
	useEffect(() => {
		unmountedRef.current = false;
		return () => {
			unmountedRef.current = true;
		};
	}, []);

	const publish = useCallback(
		async (name: string, data: unknown): Promise<void> => {
			if (!channel) {
				throw new Error(`Channel not available - cannot send message: ${name}`);
			}

			if (initialCheckRef.current && !connectedRef.current) {
				throw createConnectionError();
			}

			const logger = getRuntimeConfig().logger;
			let lastError: Error | undefined;
			for (let attempt = 0; attempt <= ABLY_CONFIG.maxPublishRetries; attempt++) {
				if (unmountedRef.current) throw lastError ?? createConnectionError();
				try {
					await channel.publish(name, data);
					return;
				} catch (err) {
					lastError = err as Error;
					logger.warning("INFO", `Publish attempt ${attempt + 1} failed for ${name}`, err);
					if (attempt < ABLY_CONFIG.maxPublishRetries) {
						await new Promise((r) => setTimeout(r, ABLY_CONFIG.publishRetryDelayMs));
					}
				}
			}
			throw lastError;
		},
		[channel, createConnectionError],
	);

	return { publish };
}
